import axios from "axios";
import config from "../../services/config/homestead.json";
import { ethers } from "ethers";
import {
  emailSenderUrl,
  firebaseConfig,
  networks,
} from "../../utils/constants";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithCustomToken,
  onAuthStateChanged,
} from "firebase/auth";
import { getMessaging, getToken } from "firebase/messaging";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  uploadBytesResumable,
} from "firebase/storage";
import {
  decryptWithPasskey,
  encryptWithPasskey,
  hashTheString,
} from "@verified-network/verified-custody";
const sgMail = require("@sendgrid/mail");

export const getImagesInBase64 = async (url) => {
  return await axios({
    method: "GET",
    url: url,
  })
    .then((res) => {
      if (res.data.errors) {
        console.error("Unexpected error occured");
        return;
      } else {
        return res.data;
      }
    })
    .catch((err) => {
      console.error("Error while images: ", err);
      return;
    });
};

// Convert base64 to a Blob object
export const base64ToBlob = (base64) => {
  if (!base64) return null;

  const cleaned = base64.replace(/^data:.*;base64,/, "");
  const mime = getMimeTypeFromBase64(base64);

  const byteCharacters = atob(cleaned);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  return new Blob([byteArray], { type: mime });
};

// Generate a Blob URL from base64
export const getBase64DisplayUrl = (base64) => {
  const blob = base64ToBlob(base64);
  if (!blob) return "";
  return URL.createObjectURL(blob);
};

// Get MIME type from Base64
export const getMimeTypeFromBase64 = (base64) => {
  if (!base64) return "";

  const cleaned = base64.replace(/^data:.*;base64,/, "");
  const firstBytes = atob(cleaned).slice(0, 10);
  const bytes = [...firstBytes].map((c) => c.charCodeAt(0));

  // Magic number checks
  if (
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  )
    return "application/pdf"; // %PDF
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  )
    return "image/png";
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "image/jpeg";
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46)
    return "image/gif";
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46
  )
    return "image/webp";
  if (cleaned.trim().startsWith("PHN2Zy")) return "image/svg+xml"; // SVG

  return "application/octet-stream"; // fallback
};

export const getDownloadExt = (base64) => {
  let ext = "bin";
  const mime = getMimeTypeFromBase64(base64);
  if (mime === "application/pdf") ext = "pdf";
  else if (mime === "image/png") ext = "png";
  else if (mime === "image/jpeg") ext = "jpg";
  else if (mime === "image/gif") ext = "gif";
  else if (mime === "image/webp") ext = "webp";
  else if (mime === "image/svg+xml") ext = "svg";

  return ext;
};

// Generate JSX and HTML tags using Blob URLs
export const getBase64DisplayUrlTag = (base64, width, height) => {
  const blobUrl = getBase64DisplayUrl(base64);
  if (!blobUrl) return { jsx: null, html: "" };

  const mime = getMimeTypeFromBase64(base64);
  const isImage = mime.startsWith("image/");
  const w = width || "100%";
  const h = height || "100%";

  // JSX version
  const jsx = isImage ? (
    <img src={blobUrl} style={{ width: w, height: h }} />
  ) : (
    <iframe src={blobUrl} width={w} height={h} style={{ border: "none" }} />
  );

  // Raw HTML string version
  const html = isImage
    ? `<img src="${blobUrl}" style="width:${w}; height:${h};" />`
    : `<iframe src="${blobUrl}" width="${w}" height="${h}" style="border:none;"></iframe>`;

  return { jsx, html };
};

export const getFileSizeInMB = (fileBase64) => {
  const bytesLengthFmt = fileBase64.length * 3;
  const divider = fileBase64.endsWith("==")
    ? 2
    : fileBase64.endsWith("=1")
      ? 1
      : 0;
  const sizeInBytes = bytesLengthFmt / 4 - divider; // 1.33 times??
  return sizeInBytes / (1024 * 1024);
};

export const check5MB = (sizeInBytes) => {
  const bytes = Number(sizeInBytes);
  // check if file > 5 MB
  return bytes > 5 * 1024 * 1024;
};

export const getAddressFromExternalId = (externalUserId) => {
  const abiCoder = new ethers.utils.AbiCoder();
  let decodedData;
  try {
    decodedData = externalUserId
      ? abiCoder.decode(["string", "string"], externalUserId)
      : ["", ""];
  } catch (err) {
    const seperator = "|";
    const decodedPacked = externalUserId
      ? Buffer.from(externalUserId?.slice(2), "hex").toString()
      : ""; // bytes → string
    decodedData =
      decodedPacked === "" ? ["", ""] : decodedPacked.split(seperator);
  }

  return decodedData ? decodedData[0] : "";
};

export const changeApllicantName = async (
  applicantId,
  firstName,
  lastName,
  middleName,
) => {
  return await axios({
    method: "POST",
    url: config.approvalEndpoint,
    headers: {
      Accept: "application/json",
    },
    data:
      middleName !== ""
        ? {
            applicantId: applicantId,
            firstName: firstName,
            middleName: middleName,
            lastName: lastName,
          }
        : {
            applicantId: applicantId,
            firstName: firstName,
            lastName: lastName,
          },
  })
    .then((res) => {
      const response = res?.data;
      if (response) {
        // console.log("return from name change request: ", response);
        return true;
      }
    })
    .catch((err) => {
      //handle request error
      console.log(
        `post request to update name failed with error: ${err?.message}`,
      );
      return false;
    });
};

export const approveOrRejectApplicant = async (
  applicantId,
  inspectionId,
  externalUserData,
  reviewResult,
) => {
  return await axios({
    method: "POST",
    url: config.approvalEndpointWebhook,
    headers: {
      Accept: "application/json",
    },
    data: reviewResult
      ? {
          applicantId: applicantId,
          inspectionId: inspectionId,
          externalUserId: externalUserData,
          reviewResult: reviewResult,
        }
      : {
          applicantId: applicantId,
          inspectionId: inspectionId,
          externalUserId: externalUserData,
        },
  })
    .then((res) => {
      const response = res?.data;
      if (response) {
        // console.log("return from req: ", response);
        return true;
      }
    })
    .catch((err) => {
      //handle request error
      console.log(`post request failed with error: ${err}`);
      return false;
    });
};

export const sendMessageToApplicant = async (
  applicantEmail,
  applicantName,
  messageSubject,
  message,
  type,
) => {
  if (applicantEmail?.length > 0 && type?.length > 0) {
    return await axios({
      url: emailSenderUrl,
      method: "POST",

      data: {
        recipient: applicantEmail,
        type,
        subject: messageSubject,
        message,
        name: applicantName,
      },
    })
      .then((res) => {
        if (res?.data) {
          return true;
        } else {
          return false;
        }
      })
      .catch((err) => {
        console.error("send email failed with error: ", err?.message);

        return false;
      });
  } else {
    return false;
  }
};

export const getApplicantMessages = async (applicantEmail) => {
  return await axios({
    method: "GET",
    url: config.sendGridMessagesEndpoint,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${config.keys.sendGridApiKey}`,
      "Content-Type": "application/json",
    },
  })
    .then((res) => {
      const response = res?.data;
      if (response) {
        return response;
      } else {
        return [];
      }
    })
    .catch((err) => {
      //handle request error
      console.log(`post request failed with error: ${err}`);
      return null;
    });
};

function getUniqueValues(arr) {
  const seen = new Set();
  return arr.filter((item) => {
    if (seen.has(item)) return false;
    seen.add(item);
    return true;
  });
}

export const fixApplicantRejections = async (
  applicantId,
  externalUserData,
  rejectionData,
) => {
  if (
    rejectionData &&
    Object.keys(rejectionData)?.length > 0 &&
    applicantId &&
    externalUserData
  ) {
    let toReset = [];
    let isDuplicate = false;
    Object.keys(rejectionData).map((key) => {
      if (key?.toString()?.toLowerCase()?.includes("photo")) {
        toReset.push("IDENTITY");
      }

      if (key?.toString()?.toLowerCase()?.includes("document")) {
        toReset.push("IDENTITY");
      }

      if (key?.toString()?.toLowerCase()?.includes("selfie")) {
        toReset.push("SELFIE");
      }

      if (key?.toString()?.toLowerCase()?.includes("video")) {
        toReset.push("SELFIE");
      }

      if (key?.toString()?.toLowerCase()?.includes("regulationsviolations")) {
        toReset.push("IDENTITY");
        if (
          key?.toString()?.toLowerCase() === "regulationsviolations_duplicate"
        ) {
          isDuplicate = true;
        }
      }

      if (key?.toString()?.toLowerCase()?.includes("_incorrect")) {
        toReset.push("IDENTITY");
      }

      if (key?.toString()?.toLowerCase()?.includes("doc")) {
        toReset.push("IDENTITY");
      }

      if (key?.toString()?.toLowerCase()?.includes("proofofaddress")) {
        toReset.push("IDENTITY");
      }

      if (key?.toString()?.toLowerCase() === "spam") {
        toReset.push("PHONE_VERIFICATION");
        toReset.push("EMAIL_VERIFICATION");
      }

      // if (key?.toString()?.toLowerCase()?.includes("ekyc")) {
      //   toReset.push("E_KYC");
      // }
    });

    const uniqueReset = getUniqueValues(toReset);

    if (uniqueReset?.length > 0) {
      try {
        const res = await axios.post(
          config.approvalEndpoint,
          JSON.stringify({
            applicantId: applicantId,
            externalUserId: externalUserData,
            dataToReset: ["ALL"],
          }),
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          },
        );
        if (res?.data?.link?.length > 0) {
          return {
            link: res?.data?.link,
            isDuplicate,
            uniqueReset,
          };
        } else {
          return {
            error: "no link received",
            link: "",
            isDuplicate,
            uniqueReset,
          };
        }
      } catch (err) {
        return {
          error: "rejection fix failed",
          link: "",
          isDuplicate,
          uniqueReset,
        };
      }
    } else {
      return {
        error: "rejection not determined",
        link: "",
        isDuplicate,
        uniqueReset,
      };
    }
  } else {
    return {
      error: "invalid rejection reasons",
      link: "",
      isDuplicate: false,
      uniqueReset: [],
    };
  }
};

export const initFireBase = () => {
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const messaging = getMessaging(app);
  const firestore = getFirestore(app);
  const storage = getStorage(app);

  return { auth, messaging, firestore, app, storage };
};

export const authenticateFirestore = async (
  hashedVaultId,
  passKeyIdHash,
  returnAuth,
) => {
  try {
    const { auth } = initFireBase();

    const authenticateRes = await axios({
      url: emailSenderUrl,
      method: "POST",

      data: {
        userId: hashedVaultId,
        type: "notify_init",
        password: passKeyIdHash,
        db: "verifiedKyc",
      },
    });

    if (authenticateRes?.data?.token) {
      const sigInCred = await signInWithCustomToken(
        auth,
        authenticateRes?.data?.token,
      );
      const userIdToken = await sigInCred?.user?.getIdToken();
      return returnAuth ? userIdToken : true;
    } else {
      return false;
    }
  } catch (err) {
    if (process.env.REACT_APP_NODE_ENV !== "production") {
      console.error("authenticate firestore failed with error: ", err?.message);
    }

    return false;
  }
};

export const getAndSaveTokenKyc = async (hashedVaultId) => {
  const { auth, messaging, firestore } = initFireBase();

  const saveFcmToken = async (hashedVaultId) => {
    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAP_ID,
        serviceWorkerRegistration: registration,
      });

      const currentToken = token;
      if (!currentToken) {
        console.warn("No FCM token available");
        return;
      }

      return true;
    } catch (err) {
      console.error("Error saving FCM token:", err);
      return false;
    }
  };

  await saveFcmToken(hashedVaultId);

  onAuthStateChanged(auth, async () => {
    await saveFcmToken(hashedVaultId);
  });
};

export const getUser = async (hashedUserAddress, hashedUserId) => {
  try {
    const authenticated = await authenticateFirestore(
      hashedUserAddress,
      hashedUserId,
    );
    if (authenticated) {
      // await getAndSaveTokenKyc(hashedUserAddress);
      const { firestore } = initFireBase();
      const userDoc = doc(firestore, "verifiedKyc", hashedUserAddress);

      const docSnap = await getDoc(userDoc);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // console.log("data keys length: ", Object.keys(data || {})?.length);
        return data;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (err) {
    console.error("error fetching user:", err);
    return null;
  }
};

export const saveKycQuestionnaire = async (
  userAddress,
  hashedUserId,
  chainId,
  signature,
  kycData,
  nonce,
) => {
  try {
    if (signature) {
      return await axios({
        method: "POST",
        url: emailSenderUrl,
        data: {
          type: "save_kyc",
          walletAddress: userAddress,
          publicKey: "",
          signature,
          nonce,
          chainId,
          chainName: networks[Number(chainId)]?.chainName || "",
          kycData,
          userId: hashedUserId,
        },
      })
        .then((res) => {
          if (res?.data?.saved) {
            return true;
          } else {
            return false;
          }
        })
        .catch((err) => {
          console.log("decrypting kyc data failed with error: ", err?.message);
          return false;
        });
    }

    return false;
  } catch (err) {
    console.error("save ky error: ", err?.message);
    return false;
  }
};

export const uploadFileToFirebase = async (
  hashedUserAddress,
  file,
  fileName,
) => {
  try {
    const formData = new FormData();
    formData.append("file", file, fileName);
    formData.append("type", "save_kyc_file");

    const authenticated = await authenticateFirestore(
      hashedUserAddress,
      hashTheString(""),
      true,
    );

    if (authenticated) {
      return await axios({
        url: emailSenderUrl,
        method: "POST",
        data: {
          uniqueId: authenticated,
          type: "generate",
        },
      })
        .then(async (res) => {
          if (res?.data?.token) {
            return await axios({
              url: emailSenderUrl,
              method: "POST",
              headers: {
                Authorization: `Bearer ${res?.data?.token}`,
              },
              data: formData,
            })
              .then((_res) => {
                if (_res?.data?.fileUrl) {
                  return _res?.data?.fileUrl;
                } else {
                  return null;
                }
              })
              .catch((err) => {
                console.error("upload failed with error: ", err?.message);

                return null;
              });
          } else {
            return null;
          }
        })
        .catch((err) => {
          console.error("upload failed with error: ", err?.message);
          return null;
        });
    } else {
      return null;
    }
  } catch (err) {
    console.error("Error while uploading file:", err?.message);
    return null;
  }
};

export const encryptKycString = async (plainText) => {
  return await axios({
    method: "POST",
    url: emailSenderUrl,
    data: {
      type: "encrypt",
      plainText,
    },
  })
    .then((res) => {
      if (res?.data?.encrypted) {
        return res?.data?.encrypted;
      } else {
        return null;
      }
    })
    .catch((err) => {
      console.log("encrypting plainText failed with error: ", err?.message);
      return null;
    });
};

export const getKycChallenge = async (userAddress) => {
  return await axios({
    method: "POST",
    url: emailSenderUrl,
    data: {
      type: "kyc_challenge",
      walletAddress: userAddress,
    },
  })
    .then((res) => {
      if (res?.data?.message && res?.data?.nonce) {
        return {
          message: res?.data?.message,
          nonce: res?.data?.nonce,
        };
      } else {
        return null;
      }
    })
    .catch((err) => {
      console.log("get decrypt challenge failed with error: ", err);
      return null;
    });
};

export const decryptUserKycData = async (
  userAddress,
  chainId,
  kycData,
  signature,
  nonce,
) => {
  try {
    if (signature) {
      return await axios({
        method: "POST",
        url: emailSenderUrl,
        data: {
          type: "decrypt_kyc",
          walletAddress: userAddress,
          publicKey: "",
          signature,
          nonce,
          chainId,
          kycData,
          chainName: networks[Number(chainId)]?.chainName || "",
        },
      })
        .then((res) => {
          if (res?.data?.kycData) {
            console.log(
              "decrypt keys length: ",
              Object.keys(res?.data?.kycData || {})?.length,
            );
            return res?.data?.kycData;
          } else {
            return null;
          }
        })
        .catch((err) => {
          console.log("decrypting kyc data failed with error: ", err?.message);
          return null;
        });
    }

    return null;
  } catch (err) {
    return null;
  }
};
